import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkFailed, ClerkLoaded, ClerkLoading, ClerkProvider, useClerk } from '@clerk/clerk-react';
import './styles.css';
import { App } from './App';
import { setTokenGetter } from './authFetch';

// The admin center shares the tavezio.io Clerk instance with order. and apps. (one sign-in via the
// .tavezio.io cookies). Clerk's session token lives about 60 s and only Clerk JS refreshes it, so
// the shell loads Clerk itself and sends that token on every API call (authFetch). Without this a
// tab died about a minute after load: every call 401'd until a full reload.
// The key is inlined at build (admin/Dockerfile maps the service's CLERK_PUBLISHABLE_KEY). Unset
// (local dev, preview) = no Clerk: the BFF runs open and the shell goes straight to the app.
const publishableKey: string = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '';

// Registers the session token getter before App's first request. It is set during render, not in
// an effect, because App's own effects (its first /me) run before a parent's effects do.
function WithSessionToken({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  setTokenGetter(async (opts) => (clerk.session ? clerk.session.getToken(opts) : null));
  return <>{children}</>;
}

function Root() {
  if (!publishableKey) return <App />;
  return (
    <ClerkProvider publishableKey={publishableKey}>
      <ClerkLoading>
        <div className="boot">Loading…</div>
      </ClerkLoading>
      <ClerkLoaded>
        <WithSessionToken>
          <App />
        </WithSessionToken>
      </ClerkLoaded>
      {/* Clerk's script did not load (a firewall or blocker stopping clerk.tavezio.io). Run on the
          session cookie alone, as before Clerk was loaded here: it works until the cookie expires. */}
      <ClerkFailed>
        <App />
      </ClerkFailed>
    </ClerkProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
